<template>
    <div class="wrap">
        <h4 class="text-center text-uppercase mt-2">{{ title }}</h4>

        <div class="chart-container" style="height: 400px; width: 100%; border-radius: 8px; overflow: hidden; background-color: #ffffff;">
            <div ref="chart" style="width: 100%; height: 100%;"></div>
        </div>
    </div>
</template>

<script>
    import * as echarts from 'echarts';

    export default {
        name: "OEEChart",
        props: {
            title: String,
            dataset: Object,
            type: {
                type: String,
                default: 'hourly'
            }
        },
        data() {
            return {
                chartInstance: null
            };
        },
        watch: {
            dataset: {
                handler(newVal) {
                    if (this.chartInstance && newVal && newVal.labels) {
                        this.updateChart(newVal);
                    }
                },
                deep: true
            },
            type() {
                if (this.chartInstance && this.dataset && this.dataset.labels) {
                    this.updateChart(this.dataset);
                }
            }
        },
        mounted() {
            this.initChart();
            window.addEventListener('resize', this.handleResize);
        },
        beforeDestroy() {
            if (this.chartInstance) {
                this.chartInstance.dispose();
            }
            window.removeEventListener('resize', this.handleResize);
        },
        methods: {
            initChart() {
                const chartDom = this.$refs.chart;
                this.chartInstance = echarts.init(chartDom);
                if (this.dataset && this.dataset.labels) {
                    this.updateChart(this.dataset);
                }
            },
            handleResize() {
                if (this.chartInstance) {
                    this.chartInstance.resize();
                }
            },
            updateChart(data) {
                const option = {
                    backgroundColor: '#ffffff',
                    tooltip: {
                        trigger: 'axis',
                        axisPointer: { type: 'cross', label: { backgroundColor: '#6a7985' } },
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        borderColor: '#ccc',
                        borderWidth: 1,
                        textStyle: { color: '#333' },
                        formatter: function (params) {
                            let tooltip = params[0].name + '<br />';
                            params.forEach(param => {
                                tooltip += param.marker + ' ' + param.seriesName + ': ' + parseFloat(param.value).toFixed(2) + ' %<br />';
                            });
                            return tooltip;
                        }
                    },
                    legend: {
                        data: ['OEE', 'Availability', 'Performance', 'Quality'],
                        textStyle: { color: '#333' },
                        bottom: 0,
                        padding: [0, 0, 20, 0]
                    },
                    grid: {
                        left: '3%',
                        right: '4%',
                        bottom: '10%',
                        top: '10%',
                        containLabel: true
                    },
                    xAxis: {
                        type: 'category',
                        boundaryGap: false,
                        data: data.labels,
                        axisLabel: {
                            color: '#666',
                            hideOverlap: true,
                            rotate: this.type === 'weekly' ? 30 : 0,
                            formatter: value => this.formatAxisLabel(value)
                        },
                        axisLine: { lineStyle: { color: '#ccc' } }
                    },
                    dataZoom: [
                        { type: 'inside' }
                    ],
                    yAxis: {
                        type: 'value',
                        axisLabel: { color: '#666', formatter: '{value} %' },
                        axisLine: { show: false },
                        splitLine: { lineStyle: { color: '#eee' } },
                        max: 100
                    },
                    series: [
                        this.createSeries('OEE', data.oee, '#605e5e'),
                        this.createSeries('Availability', data.availability, '#03A9F4'),
                        this.createSeries('Performance', data.performance, '#8BC34A'),
                        this.createSeries('Quality', data.quality, '#FF9800')
                    ]
                };
                this.chartInstance.setOption(option);
            },
            // F-04: the backend sends full date-time range strings as labels
            // (e.g. "01 Jun 2026 00:00:00 - 01 Jun 2026 00:59:59"), which are
            // unreadable on the x-axis for hourly/weekly. Shorten them per type;
            // the full range is still shown in the tooltip (params[0].name).
            formatAxisLabel(value) {
                if (value === null || value === undefined) {
                    return value;
                }
                const label = String(value);
                if (this.type === 'hourly') {
                    // show only the start time HH:mm of the hour bucket
                    const match = label.match(/(\d{1,2}:\d{2})(?::\d{2})?/);
                    return match ? match[1] : label;
                }
                if (this.type === 'weekly') {
                    // show only the week-start date (before the range separator)
                    return label.split(/\s+[–-]\s+/)[0];
                }
                // daily / monthly labels are already short
                return label;
            },
            createSeries(name, data, color) {
                return {
                    name: name,
                    type: 'line',
                    smooth: true,
                    symbol: 'circle',
                    symbolSize: 8,
                    data: data,
                    itemStyle: { color: color },
                    lineStyle: { width: 3 },
                    animationDuration: 2000,
                    animationEasing: 'cubicOut'
                };
            }
        }
    }
</script>

<style scoped>
    .chart {
        height: 400px;
    }
</style>
